import React, { useEffect, useState, useCallback } from 'react';
import type { PolicyViewModel } from '../../api/types/policy';

export type PolicyContainerProps = {
  /** Fetches the policy data (e.g. from terms-of-service, privacy-policy, listing-agreement endpoints). */
  fetchPolicy: () => Promise<PolicyViewModel | null>;
  /** Optional fallback title when policy has no name. */
  fallbackTitle?: string;
};

/**
 * Displays a single policy: loads data via fetchPolicy, shows loading/error states,
 * and renders policy name and text (supports HTML content).
 */
const PolicyContainer: React.FC<PolicyContainerProps> = ({
  fetchPolicy,
  fallbackTitle = 'Sözleşme',
}) => {
  const [policy, setPolicy] = useState<PolicyViewModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPolicy(null);
    try {
      const data = await fetchPolicy();
      setPolicy(data ?? null);
      if (data == null) {
        setError('İçerik bulunamadı.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'İçerik yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [fetchPolicy]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (error || !policy) {
    return (
      <div className="alert alert-warning mb-0" role="alert">
        {error ?? 'İçerik bulunamadı.'}
      </div>
    );
  }

  const title = policy.name?.trim() || fallbackTitle;
  const text = policy.text ?? '';

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-4">
        <h1 className="h4 card-title mb-4">{title}</h1>
        <div
          className="policy-content text-secondary"
          dangerouslySetInnerHTML={{ __html: text || '' }}
        />
      </div>
    </div>
  );
};

export default PolicyContainer;
